import { OfferCardSalesPerson } from "./offer-card-salesperson";
import React from "react";
import { useTranslation } from "react-i18next";
import { useUTCDate } from "../../../../../shared/infrastructure/ui/hooks/use-utc-date";
import { EquipmentTypeT, OfferT } from "../../../domain";
import { LinkIcon, TagIcon } from "../../../../../shared/infrastructure";
import {
  OfferCardMarginTooltip,
  OfferCardMarginTooltipContainer,
} from "./offer-card-margin-tooltip";

type DataItemProps = {
  label: string;
  value: string;
};

const DataItem = ({ label, value }: DataItemProps): React.ReactElement => {
  const [t] = useTranslation("global");

  return (
    <section className="flex justify-between md:flex md:gap-x-2 lg:mr-2 xl:grid xl:justify-start">
      <span
        className="items-center overflow-hidden text-ellipsis whitespace-nowrap text-xs font-normal text-[#9E9E9E] lg:mr-1 lg:max-w-[54px] lg:whitespace-normal xl:mr-0 xl:max-w-none xl:whitespace-nowrap"
        title={label}
      >
        {t<string>(
          `context.private.components.offer-card-information.${label}`
        )}
      </span>
      <span
        className={`items-center  text-ellipsis whitespace-nowrap text-xs font-bold text-[#383F48] md:text-sm  lg:flex xl:block`}
        title={value}
      >
        {value}
      </span>
    </section>
  );
};

type CarrierProps = {
  carrierName: string;
  className?: string;
  carrierId: string;
  carrierMcNumber: string;
  carrierDotNumber: string;
  carrierHighwayId: string;
};

const Carrier = ({
  carrierName,
  carrierId,
  className,
  carrierMcNumber,
  carrierDotNumber,
  carrierHighwayId,
}: CarrierProps): React.ReactElement => (
  <section
    className={[
      "my-2 grid content-between gap-1 md:my-0 md:mr-9 xl:mr-0",
      className,
    ].join(" ")}
  >
    <a
      target="_blank"
      rel="noopener noreferrer"
      href={`https://gohighway.com/broker/carriers/${carrierHighwayId}`}
      className="grid cursor-pointer content-start gap-1"
    >
      <section className="hidden grid-flow-col items-center justify-between gap-1 border-b-[1px] border-solid border-b-[#3C7CF3] xl:grid">
        <span
          title={carrierName}
          className="overflow-hidden text-ellipsis whitespace-nowrap px-[5px] text-sm font-[700] capitalize text-[#3C7CF3]"
        >
          {carrierName.toLowerCase()}
        </span>
        <LinkIcon width={20} height={20} fill="#3C7CF3" />
      </section>
      <span
        title={carrierId}
        className="hidden overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[#484848] xl:block xl:px-[5px]"
      >
        {carrierId}
      </span>

      {/* Mobile and Tablets Only */}
      <div className="flex items-center justify-between xl:hidden">
        <div className="flex flex-col justify-center">
          <section className="flex items-center justify-between gap-3 border-b-[1px] border-solid border-b-[#3C7CF3]">
            <span
              title={carrierName}
              className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-[700] capitalize text-[#3C7CF3]"
            >
              {carrierName.toLowerCase()}
            </span>
            <LinkIcon width={20} height={20} fill="#3C7CF3" />
          </section>
          <span
            title={carrierId}
            className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[#484848]"
          >
            {carrierId}
          </span>
        </div>

        <div className="flex flex-col">
          <div className="flex w-24 flex-col xl:hidden">
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">MC</span>
              <span title={carrierMcNumber} className="text-[10px] font-medium">
                {carrierMcNumber}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-xs text-gray-400">DOT</span>
              <span title={carrierDotNumber} className="text-xs font-medium">
                {carrierDotNumber}
              </span>
            </div>
          </div>
        </div>
      </div>
    </a>

    {/* From Desktop large above */}
    <section className="hidden grid-cols-[1fr_1fr] justify-around gap-[16px] rounded-[5px] bg-[#D6D6D6] px-[16px] py-[6px] xl:grid">
      <section className="grid gap-1">
        <span
          title={carrierMcNumber}
          className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-[500] text-[#484848]"
        >
          {carrierMcNumber}
        </span>
        <span className="text-[8px] text-[#919191]">MC</span>
      </section>
      <section className="grid gap-1">
        <span
          title={carrierDotNumber}
          className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-[500] text-[#484848]"
        >
          {carrierDotNumber}
        </span>
        <span className="text-[8px] text-[#919191]">DOT</span>
      </section>
    </section>
  </section>
);

type CustomerProps = {
  customerName: string | null;
  customerId: string | null;
  rate: number | null;
  className?: string;
  offer: OfferT;
  showDetails?: boolean;
  salesPersonName?: string;
};

type OfferCardCustomerRateProps = {
  rate: number | null;
  showDetails?: boolean;
  salesPersonName?: string;
  offer: OfferT;
  children?: React.ReactElement;
};

const OfferCardCustomerRate = ({
  rate,
  children,
  showDetails,
  salesPersonName,
  offer,
}: OfferCardCustomerRateProps): React.ReactElement => {
  const [t] = useTranslation("global");

  return (
    <React.Fragment>
      {/* Mobile Only */}
      <div
        className={[
          showDetails ? "hidden" : "flex",
          "relative justify-between md:hidden",
        ].join(" ")}
      >
        <div className="flex items-center gap-x-1 md:hidden">
          <div className="flex items-center gap-x-[6px]">
            <TagIcon width={8} height={8} fill="black" />
            <span className="text-sm font-[400] leading-normal text-black md:text-sm xl:text-sm">
              {t<string>("context.private.components.offer-card-fare.rate")}
            </span>
          </div>
          <span className="text-sm font-bold leading-normal tracking-tighter text-black md:text-xs xl:text-sm">
            {rate === 0
              ? "No cap"
              : rate
              ? `$ ${rate.toLocaleString("en-US")} USD`
              : "- -"}
          </span>
        </div>

        {/* LogButton (Mobile versions use it only) */}
        <div className="absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4">
          {children}
        </div>

        <section className="flex">
          <OfferCardSalesPerson salesPersonName={salesPersonName ?? "--"} />
        </section>
      </div>

      <OfferCardMarginTooltipContainer>
        <section
          className={[
            "hidden justify-between md:flex md:h-[30px] md:flex-col md:gap-1 lg:flex-row xl:grid xl:h-auto xl:grid-cols-[12px_1fr_auto] xl:flex-col xl:items-center",
            "lg:items-center xl:items-start",
          ].join(" ")}
        >
          <div className="flex items-center">
            <TagIcon width={12} height={12} fill="black" />
            <span className="text-sm font-[400] text-black md:ml-1 md:text-xs xl:ml-0 xl:text-sm">
              {t<string>("context.private.components.offer-card-fare.rate")}
            </span>
          </div>
          <span className="text-sm font-bold text-black md:ml-[14px] md:text-xs lg:ml-0 lg:whitespace-nowrap xl:ml-6  xl:whitespace-normal xl:text-sm">
            {rate === 0
              ? "No cap"
              : rate
              ? `$${rate.toLocaleString("en-US")} USD`
              : "- -"}
          </span>
        </section>
        <OfferCardMarginTooltip offer={offer} />
      </OfferCardMarginTooltipContainer>
    </React.Fragment>
  );
};

const Customer = ({
  customerName,
  customerId,
  rate,
  className,
  showDetails,
  salesPersonName,
  offer,
}: CustomerProps): React.ReactElement => {
  return (
    <section
      className={[
        "grid content-between gap-3 2xl:grid-cols-[auto_auto] 2xl:content-end 2xl:gap-[20px]",
        className,
      ].join(" ")}
    >
      {customerName !== null && customerId !== null && (
        // Desktop full
        <section className="hidden gap-1 md:flex md:gap-x-2 xl:grid">
          <span className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold capitalize md:text-[#3C7CF3] xl:text-black">
            {customerName.toLowerCase()}
          </span>
          <span className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap text-[10px] text-[#484848]">
            {customerId}
          </span>
        </section>
      )}
      {
        /* !!rate && */ <div className="md:hidden xl:flex">
          <OfferCardCustomerRate
            offer={offer}
            rate={rate}
            showDetails={showDetails}
            salesPersonName={salesPersonName}
          />
        </div>
      }
    </section>
  );
};

type OfferCardInformationProps = {
  offer: OfferT;
  showDetails: boolean;
  isDedicated: boolean;
};

type MobileDataItemsProps = {
  weight: number | null;
  equipment_type: EquipmentTypeT | null;
  revenue_code_id: string | null;
  ordered_date: string | null;
};

const MobileDataItems = ({
  weight,
  equipment_type,
  revenue_code_id,
  ordered_date,
}: MobileDataItemsProps): React.ReactElement => {
  const { dateFromUTC } = useUTCDate();

  return (
    <section className="flex justify-between border-t border-b border-dashed border-[#9E9E9E] py-3 md:mr-9 md:border-none md:py-0 xl:hidden">
      <div className="flex w-[139px] flex-col">
        <DataItem
          label="equipment-types"
          value={equipment_type?.description ?? "- -"}
        />
        <DataItem
          label="weight"
          value={`${(weight ?? 0).toLocaleString("en-US")} lb`}
        />
      </div>

      <div className="flex w-[139px] flex-col">
        <DataItem label="revenue-code" value={revenue_code_id ?? "- -"} />
        <DataItem
          label="ordered-date"
          value={
            ordered_date
              ? dateFromUTC(ordered_date).format("MM/DD/YY HH:mm")
              : "- -"
          }
        />
      </div>
    </section>
  );
};

const OfferCardInformation = ({
  offer,
  showDetails,
  isDedicated,
}: OfferCardInformationProps): React.ReactElement => {
  const {
    equipment_type,
    weight,
    revenue_code_id,
    ordered_date,
    carrier,
    customer,
    rate,
  } = offer;
  const { dateFromUTC } = useUTCDate();

  return (
    <section
      className={[
        showDetails ? "flex flex-col" : "hidden",
        "xl:grid xl:grid-cols-[1fr_138px_138px] xl:justify-between xl:gap-[80px] 2xl:grid-cols-[1fr_138px_250px]",
      ].join(" ")}
    >
      {/* Desktop */}
      <section className="hidden grid-cols-[auto_auto] content-start gap-[12px_42px] pt-4 xl:grid 2xl:grid-cols-[auto_auto_auto_auto] 2xl:content-end 2xl:gap-[12px_20px]">
        <DataItem
          label="equipment-types"
          value={equipment_type?.description ?? "- -"}
        />

        {!isDedicated && (
          <React.Fragment>
            <DataItem
              label="weight"
              value={`${(weight ?? 0).toLocaleString("en-US")} lb`}
            />
            <DataItem label="revenue-code" value={revenue_code_id ?? "- -"} />
            <DataItem
              label="ordered-date"
              value={
                ordered_date
                  ? dateFromUTC(ordered_date).format("MM/DD/YY HH:mm")
                  : "- -"
              }
            />
          </React.Fragment>
        )}
      </section>

      {/* Mobile Only */}
      <div className="md:hidden">
        <Carrier
          carrierId={carrier.carrier_id ?? "- -"}
          carrierName={carrier.name ?? "- -"}
          carrierHighwayId={carrier.highway_id ?? ""}
          carrierMcNumber={!carrier.mc_number ? "- -" : carrier.mc_number}
          carrierDotNumber={!carrier.dot_number ? "- -" : carrier.dot_number}
        />
      </div>

      {/* Mobile Only */}
      <MobileDataItems
        equipment_type={equipment_type}
        weight={weight}
        ordered_date={ordered_date}
        revenue_code_id={revenue_code_id}
      />

      {/* Desktop */}
      <Carrier
        className="hidden xl:grid"
        carrierId={carrier.carrier_id ?? "- -"}
        carrierName={carrier.name ?? "- -"}
        carrierHighwayId={carrier.highway_id ?? ""}
        carrierMcNumber={!carrier.mc_number ? "- -" : carrier.mc_number}
        carrierDotNumber={!carrier.dot_number ? "- -" : carrier.dot_number}
      />
      <Customer
        className="hidden xl:grid"
        offer={offer}
        customerId={customer?.customer_id ?? "- -"}
        customerName={customer?.name ?? "- -"}
        rate={rate}
      />
      {/*  */}
    </section>
  );
};

type RepCardInformationProps = {
  offer: OfferT;
  showDetails: boolean;
};

const RepCardInformation = ({
  offer,
  showDetails,
}: RepCardInformationProps): React.ReactElement => {
  const {
    equipment_type,
    weight,
    revenue_code_id,
    carrier,
    rate,
    commodity,
    ordered_date,
  } = offer;

  const [t] = useTranslation("global");
  const { dateFromUTC } = useUTCDate();

  return (
    <section
      className={[
        showDetails ? "flex flex-col md:hidden" : "hidden",
        "justify-between gap-[16px]",
      ].join(" ")}
    >
      {/* Desktop */}
      <section className="hidden xl:grid xl:grid-flow-col xl:gap-[30px]">
        <DataItem
          label="equipment-types"
          value={equipment_type?.description ?? "- -"}
        />
        <DataItem
          label="ordered-date"
          value={
            ordered_date
              ? dateFromUTC(ordered_date).format("MM/DD/YY HH:mm")
              : "- -"
          }
        />
        <DataItem
          label="weight"
          value={`${(weight ?? 0).toLocaleString("en-US")} lb`}
        />
        <DataItem label="revenue-code" value={revenue_code_id ?? "- -"} />
        <DataItem label="commodity" value={commodity} />
      </section>

      {/* Mobile */}
      <div className="md:hidden">
        <Carrier
          carrierId={carrier.carrier_id ?? "- -"}
          carrierName={carrier.name ?? "- -"}
          carrierHighwayId={carrier.highway_id ?? ""}
          carrierMcNumber={!carrier.mc_number ? "- -" : carrier.mc_number}
          carrierDotNumber={!carrier.dot_number ? "- -" : carrier.dot_number}
        />
      </div>

      {/* Mobile Only */}
      <MobileDataItems
        equipment_type={equipment_type}
        weight={weight}
        ordered_date={ordered_date}
        revenue_code_id={revenue_code_id}
      />

      {/* Desktop */}
      <section className="hidden items-center xl:grid xl:grid-cols-[12px_1fr_auto] xl:items-center xl:gap-[4px]">
        <div className="flex items-center">
          <TagIcon width={12} height={12} fill="black" />
          <span className="text-[12px] font-[400] text-black">
            {t<string>("context.private.components.offer-card-fare.rate")}
          </span>
        </div>
        <span className="text-[12px] font-[700] text-black md:ml-auto">
          {rate === 0
            ? "No cap"
            : rate
            ? `$${rate.toLocaleString("en-US")} USD`
            : "- -"}
        </span>
      </section>
    </section>
  );
};

export {
  OfferCardInformation,
  Carrier,
  RepCardInformation,
  MobileDataItems,
  DataItem,
  Customer,
  OfferCardCustomerRate,
};
